<template>
  <div class="grid-content" ref="campaignsTableContainer">
    <b-table class="campaigns-table" backend-sorting default-sort="Active" :default-sort-direction="'desc'"
      draggable-column hoverable ref="campaignsTable" sticky-header :backend-filtering="true"
      :data="campaigns && campaigns.Data ? campaigns.Data : []" :key="tableDataKey"
      :total="campaigns ? campaigns.Total : 0" @columndragstart="columndragstart" @columndrop="columndrop"
      @columndragover="columndragover" @columndragleave="columndragleave" @filters-change="onFiltersChange"
      @sort="onPageSort">
      <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class"
        :label="column.label" :visible="column.visible && !column.forbidden && column.id !== 1" :width="column.width"
        :sortable="column.sortable" :searchable="column.searchable" :header-class="'column-th-' + column.name">

        <template v-if="column.searchable" #searchable="props">
          <b-input v-model="props.filters[props.column.field]" @blur="onColumnFiltersBlur"
            v-on:keyup.native.enter="onColumnFiltersEnter" />
        </template>
        <template v-slot="props">
          <div v-if="column.name === 'ChannelId'" :style="getColumnStyle(column)">
            {{ props.row[column.name] }}
          </div>
          <div v-else-if="column.name === 'ChannelTitle'" :style="getColumnStyle(column)">
            <div class="icon-w" v-if="getChannelMetadataTypeIconName(props.row.ChannelType)">
              <img :src="require('@/assets/' + getChannelMetadataTypeIconName(props.row.ChannelType) + '.png')" />
            </div>
            <div>
              <div class="text" :class="{ active: hasAdGroups(props.row.ChannelType) }"
                @click="loadAdGroups(props.row)">{{ props.row[column.name] }}</div>
              <div class="campaign-status">
                <div class="campaign-name-status" v-if="!showRefreshTokenBtn(props.row.ChannelId)">
                  <template v-if="props.row.ChannelStatus === CampaignStatus.NotActive"><span
                      class="indicator indicator-4"></span></template>
                  <template v-else-if="props.row.ChannelStatus === CampaignStatus.Active"><span
                      class="indicator indicator-1"></span></template>
                  <template v-else-if="props.row.ChannelStatus === CampaignStatus.TokenInvalid"><span
                      class="indicator indicator-5"></span></template>
                  <template v-else-if="props.row.ChannelStatus === CampaignStatus.NotConnected"><span
                      class="indicator indicator-3"></span></template>
                  <template v-else-if="props.row.ChannelStatus === CampaignStatus.MissingPermission"><span
                      class="indicator indicator-2"></span></template>
                </div>
                <div class="status-str-text"
                  :class="{ clickable: props.row.ChannelStatus === CampaignStatus.TokenInvalid }"
                  v-if="!showRefreshTokenBtn(props.row.ChannelId)" @click="onStatusClick(props.row)">
                  {{ props.row["StatusStr"] }}
                </div>
                <div v-if="showRefreshTokenBtn(props.row.ChannelId)">
                  <b-button type="is-success" size="is-small" class="btn-update-token is-icon"
                    @click="refreshToken(props.row)">
                    <icon-base width="12" height="12" view-box="0 0 12 12" icon-name="update"><icon-update /></icon-base>
                    <span>{{ $t("CampaignEdit.Google.RepairToken") }}</span>
                  </b-button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="column.name === 'ChannelTypeString'" :style="getColumnStyle(column)">
            <div class="icon-w" v-if="getChannelMetadataTypeIconName(props.row.ChannelType)">
              <img :src="require('@/assets/' + getChannelMetadataTypeIconName(props.row.ChannelType) + '.png')" />
            </div>
            <div class="text">{{ props.row[column.name] }}</div>
          </div>
          <div v-else-if="column.name === 'Phone'" :style="getColumnStyle(column)">
            {{ getPhoneColumnValue(props.row) }}
          </div>
          <div v-else-if="column.name === 'StatusStr'" :style="getColumnStyle(column)" class="campaign-status">
            <div class="campaign-name-status">
              <template v-if="props.row.ChannelStatus === CampaignStatus.NotActive"><span
                  class="indicator indicator-4"></span></template>
              <template v-else-if="props.row.ChannelStatus === CampaignStatus.Active"><span
                  class="indicator indicator-1"></span></template>
              <template v-else-if="props.row.ChannelStatus === CampaignStatus.TokenInvalid"><span
                  class="indicator indicator-5"></span></template>
              <template v-else-if="props.row.ChannelStatus === CampaignStatus.NotConnected"><span
                  class="indicator indicator-3"></span></template>
              <template v-else-if="props.row.ChannelStatus === CampaignStatus.MissingPermission"><span
                  class="indicator indicator-2"></span></template>
            </div>
            <div class="status-str-text">
              {{ props.row[column.name] }}
            </div>
          </div>
          <div v-else :style="getColumnStyle(column)">
            {{ getFormattedValue(props.row[column.name], column.name) }}
          </div>
        </template>
      </b-table-column>

      <b-table-column cell-class="cell-actions" field="actions" :label="$t('Campaigns.Grid.Columns.Actions')" width="265"
        :sticky="true" v-slot="props" :visible="isActionsColumnVisible">
        <div class="actions-list">
          <b-tooltip position="is-top" type="is-dark"
            :label="props.row.Active ? $t('Campaigns.Grid.ActionsColumn.Deactivate') : $t('Campaigns.Grid.ActionsColumn.Activate')">
            <b-field>
              <b-switch v-model="props.row.Active" :disabled="props.row.IsAutoCreated"
                @input="onActiveChange($event, props.row)"></b-switch>
            </b-field>
          </b-tooltip>
          <b-tooltip v-if="!props.row.Deleted" :label="$t('Campaigns.Grid.ActionsColumn.Edit')" position="is-top"
            type="is-dark">
            <router-link :to="`${campaignEditRouteValue}?Id=${props.row.ChannelId}`" exact class="action">
              <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="pencil"><icon-pencil /></icon-base>
            </router-link>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.Grid.ActionsColumn.Alerts')" position="is-top" type="is-dark">
            <a :href="`/CampaignAlerts/Channel?Id=${props.row.ChannelId}`" class="action action-alerts">
              <icon-base width="16" height="20" view-box="0 0 16 20" icon-name="bell"><icon-bell /></icon-base>
              <span class="tag is-small" outlined v-if="props.row.AlertsCount > 0"><span
                  class="tag-i">{{ props.row.AlertsCount }}</span></span>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.Grid.ActionsColumn.History')" position="is-top" type="is-dark">
            <a :href="`/Channels/History?Id=${props.row.ChannelId}`" class="action">
              <icon-base width="21" height="18" view-box="0 0 21 18" icon-name="history"><icon-history /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.Grid.ActionsColumn.Statistics')" position="is-top" type="is-dark">
            <a :href="`/Channels/View?Id=${props.row.ChannelId}`" class="action">
              <icon-base width="22" height="12" view-box="0 0 22 12"
                icon-name="chart-line"><icon-chart-line /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.Grid.ActionsColumn.Keywords')" position="is-top" type="is-dark"
            v-if="showKeywordsAction(props.row.ChannelType)">
            <a :href="`/Channels/View?Id=${props.row.ChannelId}#${getKeywordsActionLinkHashFragment(props.row.ChannelId, props.row.ChannelType)}`"
              class="action">
              <icon-base width="20" height="20" view-box="0 0 20 20" icon-name="chart-pie"><icon-chart-pie /></icon-base>
            </a>
          </b-tooltip>
          <b-tooltip class="tooltip-phone" position="is-top" type="is-dark"
            :label="props.row.PhoneId ? $t('Campaigns.Grid.ActionsColumn.DeactivatePhone') : $t('Campaigns.Grid.ActionsColumn.ActivatePhone')">
            <div class="action action-phone">
              <span @click="togglePhone($event, props.row)">
                <icon-base v-if="!props.row.PhoneId" width="17.03" height="20" view-box="0 0 17.03 20"
                  icon-name="mobile-yes"><icon-mobile-yes /></icon-base>
                <icon-base v-else width="16.37" height="20" view-box="0 0 16.37 20"
                  icon-name="mobile-no"><icon-mobile-no /></icon-base>
              </span>
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('Campaigns.Grid.ActionsColumn.Delete')" position="is-top" type="is-dark"
            v-if="!props.row.Deleted">
            <a href="#" class="action action-delete" @click="deleteCampaign($event, props.row)">
              <icon-base width="20" height="20" view-box="0 0 512 512" icon-name="trash"><icon-trash /></icon-base>
            </a>
          </b-tooltip>
        </div>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { BComponent } from "buefy/types/components";
import { LoaderComponent } from "vue-loading-overlay";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { campaignEditRoute } from "@/router";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { Result } from "@/models/Result";
import { TableSettings } from "@/models/TableSettings";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignAdGroupGridViewModel } from "@/models/campaigns/CampaignAdGroupGridViewModel";
import { CampaignGridView, RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { ChannelType } from "@/models/campaigns/ChannelType";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import FacebookApiService from "@/services/api/FacebookApiService";
import GoogleApiService from "@/services/api/GoogleApiService";
import PhonesApiService from "@/services/api/PhonesApiService";
import CampaignsAdGroupsGridHeadline from "./CampaignsAdGroupsGridHeadline.vue";
import IconBase from "../../components/IconBase.vue";
import IconBell from "../../components/icons/IconBell.vue";
import IconChartLine from "../../components/icons/IconChartLine.vue";
import IconChartPie from "../../components/icons/IconChartPie.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconFacebook from "../../components/icons/IconFacebook.vue";
import IconGoogle from "../../components/icons/IconGoogle.vue";
import IconHistory from "../../components/icons/IconHistory.vue";
import IconMobileNo from "../../components/icons/IconMobileNo.vue";
import IconMobileYes from "../../components/icons/IconMobileYes.vue";
import IconPencil from "../../components/icons/IconPencil.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconUpdate from "../../components/icons/IconUpdate.vue";

const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsAdGroupsGridHeadline,
    IconBase,
    IconChartLine,
    IconChartPie,
    IconEye,
    IconFacebook,
    IconGoogle,
    IconPencil,
    IconBell,
    IconHistory,
    IconMobileYes,
    IconMobileNo,
    IconTrash,
    IconUpdate,
  },
})
export default class CampaignsGridContent extends Vue {

  //#region Vuex getters and actions
  @Getter("sortDir", { namespace }) sortDir!: string;
  @Getter("sortField", { namespace }) sortField!: string;
  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsLoading", { namespace }) campaignsLoading!: boolean;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("page", { namespace }) page!: number;
  @Getter("selectedCampaign", { namespace }) selectedCampaign!: CampaignGridView;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Action("setNeedToRefreshCampaignsGrid", { namespace }) setNeedToRefreshCampaignsGrid!: (needToRefreshCampaignsGrid: RefreshGridMode) => void;
  @Action("setColumns", { namespace }) setColumns!: (columns: GridColumn[]) => void;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setGraphsHiddenOnScroll", { namespace }) setGraphsHiddenOnScroll!: (graphsHiddenOnScroll: boolean) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCampaign", { namespace }) setSelectedCampaign!: (selectedCampaign?: CampaignGridView) => void;
  @Action("setSelectedCampaignId", { namespace }) setSelectedCampaignId!: (campaignClientId?: number) => void;
  @Action("setSortDir", { namespace }) setSortDir!: (sortDir: string) => void;
  @Action("setSortField", { namespace }) setSortField!: (sortField: string) => void;

  //#endregion Vuex getters and actions

  //#region Properties

  isColumnFiltersUpdated = false;
  tableDataKey = 0;
  campaignsTableContainer: Element | Window | null = null;
  campaignsTableLoader?: LoaderComponent = undefined;
  showRefreshTokenBtnDict: { [id: number]: boolean } = {};
  adGroups: CampaignAdGroupGridViewModel[] = [];

  CampaignStatus = CampaignStatus;

  //#endregion Properties

  mounted(): void {
    var campaignsTable = (this.$refs.campaignsTable as BComponent);

    if (campaignsTable) {
      this.setSortField(campaignsTable.defaultSort);
      this.setSortDir(campaignsTable.defaultSortDirection);
    }
  }

  //#region Watchers

  @Watch("campaignsLoading", { immediate: true })
  onCampaignsLoadingChange(newValue: boolean, oldValue: boolean): void {
    if (newValue) {
      this.campaignsTableLoader = this.$loading.show({
        container: this.$refs.campaignsTableContainer
      });
    } else {
      this.campaignsTableLoader?.hide();
      if (this.$el) {
        if (window.innerWidth > 768) {
          this.campaignsTableContainer = this.$el.querySelector(".campaigns-table .table-wrapper");
          if (this.campaignsTableContainer) {
            if (this.page === 1) {
              (this.campaignsTableContainer as Element).scrollTop = 0;
            }
            this.campaignsTableContainer.addEventListener('scroll', this.onScrollTable);
          }
        } else {
          this.campaignsTableContainer = window;
          if (this.page === 1) {
            window.scrollTo({ top: 0 });
          }
          this.campaignsTableContainer.addEventListener('scroll', this.onScrollWindow);
        }
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignEditRouteValue(): string {
    return campaignEditRoute;
  }

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  //#endregion Getters


  //#region Handlers

  deleteCampaign(e: Event, campaign: CampaignGridView) {
    e.preventDefault();
    HelperService.showConfirmDialog(
      undefined,
      i18n.t("AreYouSure").toString(),
      i18n.t("Yes").toString(),
      i18n.t("No").toString(),
      () => {
        let loader = this.$loading.show({
          container: this.$refs.campaignsTableContainer
        });
        CampaignsApiService.deleteCampaign(campaign.ChannelId).then((result: Result) => {
          if (result.Success) {
            if (result.Message) {
              HelperService.showMessage(i18n.t("Warning").toString(), result.Message);
            }
            if (campaign.Active && campaign.PhoneId) {
              HelperService.showMessage(i18n.t("Warning").toString(), i18n.t("Campaigns.DeactivateHasPhone").toString());
            }
            this.setNeedToRefreshCampaignsGrid(RefreshGridMode.Full);
          } else {
            HelperService.showError(result.Message);
          }
        }).finally(() => {
          loader.hide();
        });
      }
    );
  }

  onActiveChange(active: boolean, campaign: CampaignGridView): void {
    if (!active) {
      HelperService.showConfirmDialog(
        undefined,
        i18n.t("AreYouSure").toString(),
        i18n.t("OK").toString(),
        i18n.t("Cancel").toString(),
        () => {
          this.onActiveChangeInner(campaign);
        },
        () => {
          campaign.Active = true;
        }
      );
    } else {
      this.onActiveChangeInner(campaign);
    }
  }

  onActiveChangeInner(campaign: CampaignGridView): void {
    let loader = this.$loading.show({
      container: this.$refs.campaignsTableContainer
    });
    CampaignsApiService.toggleActive(campaign.ChannelId).then((result: Result) => {
      if (result.Success) {
        if (result.Message) {
          HelperService.showMessage(i18n.t("Warning").toString(), result.Message);
        }
        if (!campaign.Active && campaign.PhoneId) {
          HelperService.showMessage(i18n.t("Warning").toString(), i18n.t("Campaigns.DeactivateHasPhone").toString());
        }
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }

  onColumnFiltersBlur(e: Event) {
    if (this.isColumnFiltersUpdated) {
      this.isColumnFiltersUpdated = false;
      this.setNeedToRefreshCampaignsGrid(RefreshGridMode.Full);
    }
  }

  onColumnFiltersEnter(e: Event) {
    (e.target as HTMLInputElement).blur();
  }

  onFiltersChange(filterObject: any) {
    const filterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    for (const key of Object.keys(filterObject)) {
      const val: any = filterObject[key];
      if (val) {
        filterCollection.filters?.push({ field: key, operator: "contains", value: `'${val}'` });
      }
    }
    this.setColumnsFilters(filterCollection);
    this.isColumnFiltersUpdated = true;
  }

  onPageChange(page: number) {
    this.setPage(page);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onPageSort(fieldName: string, sortDirection: string, e: PointerEvent) {
    this.setSortDir(sortDirection);
    this.setSortField(fieldName);
    this.setPage(1);
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.GridOnly);
  }

  onScrollWindow(e: Event) {
    const target: Document = e.target as Document;
    if (this.campaigns.Total && target.documentElement.scrollTop >= document.documentElement.offsetHeight - window.innerHeight
      && this.page * CampaignService.getPageSize() < this.campaigns.Total) {
      if (this.campaignsTableContainer) {
        this.campaignsTableContainer.removeEventListener('scroll', this.onScrollWindow);
      };
      this.onPageChange(this.page + 1);
    }
  }

  onScrollTable(e: Event) {
    const target: HTMLDivElement = e.target as HTMLDivElement;
    const EXTRA_PIXELS = 2; // for browser zoom
    const contentHeight: number = target.scrollHeight - target.clientHeight - EXTRA_PIXELS;
    if (this.campaigns.Total && target.scrollTop >= contentHeight && this.page * CampaignService.getPageSize() < this.campaigns.Total) {
      if (this.campaignsTableContainer) {
        this.campaignsTableContainer.removeEventListener('scroll', this.onScrollTable);
      };
      this.onPageChange(this.page + 1);
    }
    this.setGraphsHiddenOnScroll(target.scrollTop > 10 ? true : false);
  }

  onStatusClick(campaign: CampaignGridView) {
    if (campaign.ChannelStatus === CampaignStatus.TokenInvalid) {
      this.showRefreshTokenBtnDict[campaign.ChannelId] = true;
      this.tableDataKey++;
    }
  }

  refreshToken(campaign: CampaignGridView) {
    if (CampaignService.isGoogle(campaign.ChannelType)) {
      GoogleApiService.repairToken(campaign.ChannelId);
    } else if (CampaignService.isFacebook(campaign.ChannelType)) {
      FacebookApiService.repairToken(campaign.ChannelId);
    }
  }

  togglePhone(e: Event, campaign: CampaignGridView) {
    if (!campaign.PhoneId) {
      this.$router.push(`${campaignEditRoute}?Id=${campaign.ChannelId}#phones-channel`);
    } else {
      e.preventDefault();
      HelperService.showConfirmDialog(
        undefined,
        i18n.t("Phones.Grid.ReleasePhonePopup.Message").toString(),
        i18n.t("OK").toString(),
        i18n.t("Cancel").toString(),
        () => {
          let loader = this.$loading.show({
            container: this.$refs.campaignsTableContainer
          });
          PhonesApiService.releasePhone(campaign.PhoneId as number, campaign.CompanyId).then((result: Result) => {
            if (result.Success) {
              campaign.PhoneId = undefined;
            } else {
              HelperService.showError(result.Message);
            }
          }).finally(() => {
            loader.hide();
          });
        }
      );
    }
  }

  //#endregion Handlers


  //#region Private methods

  getChannelMetadataTypeIconName(type: string): string {
    return CampaignService.getChannelMetadataTypeIconName(+type);
  }

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  getFormattedValue(value: number | string | Date, fieldName: string): string {
    if (!value) {
      return "";
    }
    if (fieldName === "GoalStartDate" || fieldName === "GoalEndDate") {
      return moment(value as Date).format("DD/MM/yyyy HH:mm");
    } else if (fieldName === "Price") {
      return i18n.locale === "he-IL" ? `₪${Math.ceil(value as number)}` : `${Math.ceil(value as number)}₪`;
    } else if (["AverageClickCost", "AverageImpressionCost", "AverageLeadCost", "AverageViewCost", "AverageVisitorCost", "Conversion", "Cost", "CTR"].includes(fieldName)) {
      return parseFloat((value as number).toFixed(2)).toString();
    } else {
      return value as string;
    }
  }

  getKeywordsActionLinkHashFragment(campaignId: number, metadataType: ChannelType): string {
    switch (metadataType) {
      case ChannelType.GoogleAdwords:
        return "keywords-view-channel";
      case ChannelType.FacebookAds:
      case ChannelType.FacebookAdLeadPage:
      case ChannelType.FacebookAdLeadCampaign:
        return "facebook-optimization-view-channel";
    }
    return "";
  }

  getPhoneColumnValue(campaign: CampaignGridView): string {
    if (campaign.ChannelType === ChannelType.WhatsAppPhone) {
      return campaign.WhatsAppPhone;
    } else {
      return campaign.Phone;
    }
  }

  hasAdGroups(metadataType: ChannelType): boolean {
    return [
      ChannelType.GoogleAdwords,
      ChannelType.FacebookAds,
      ChannelType.InstagramAds,
      ChannelType.FacebookAdLeadCampaign,
      ChannelType.InstagramAdLeadCampaign,
      ChannelType.TikTokAds,
      ChannelType.TikTokLeads,
      ChannelType.Outbrain,
    ].indexOf(metadataType) >= 0;
  }

  loadAdGroups(campaign: CampaignGridView): void {
    if (!this.hasAdGroups(campaign.ChannelType)) {
      return;
    }
    this.setSelectedCampaign(campaign);
    let loader = this.$loading.show({
      container: this.$refs.campaignsTableContainer
    });
    const dateFrom: string | undefined = this.datePeriod[0] ? moment(this.datePeriod[0]).format("YYYY-MM-DD") : undefined;
    const dateTo: string | undefined = this.datePeriod[0] ? moment(this.datePeriod[1]).format("YYYY-MM-DD") : undefined;
    CampaignsApiService.getCampaignAdGroups(campaign.ChannelId, campaign.AverageLeadCost, dateFrom, dateTo).then((result: DataSourceResult) => {
      this.adGroups = result.Data as CampaignAdGroupGridViewModel[];
      this.$emit("adgroups-loaded", this.adGroups);
    }).finally(() => {
      loader.hide();
    });
  }

  showKeywordsAction(metadataType: ChannelType): boolean {
    return [
      ChannelType.GoogleAdwords,
      ChannelType.FacebookAds,
      ChannelType.FacebookAdLeadPage,
      ChannelType.FacebookAdLeadCampaign
    ].indexOf(metadataType) >= 0;
  }

  showRefreshTokenBtn(id: number): boolean {
    return this.showRefreshTokenBtnDict[id];
  }

  //#endregion Private methods


  //#region Columns drag and drop

  draggingColumn: any = null;
  draggingColumnIndex: any = null;

  columndragstart(payload: any) {
    // clear selection of text if exists (it messes with drag and drop)
    const selectionFn: () => Selection | null = window.getSelection;
    if (selectionFn) {
      let selection: Selection | null = selectionFn();
      if (selection !== null) {
        if (selection.empty) {  // Chrome
          selection.empty();
        } else if (selection.removeAllRanges) {  // Firefox
          selection.removeAllRanges();
        }
      }
    } else if ((document as any).selection) {  // IE?
      (document as any).selection.empty();
    }

    this.draggingColumn = payload.column;
    this.draggingColumnIndex = payload.index;
    payload.event.dataTransfer.effectAllowed = 'copy';
  }

  columndragover(payload: any) {
    payload.event.dataTransfer.dropEffect = 'copy';
    payload.event.target.closest('th').classList.add('is-selected');
    payload.event.preventDefault();
  }

  columndragleave(payload: any) {
    payload.event.target.closest('th').classList.remove('is-selected');
    payload.event.preventDefault();
  }

  columndrop(payload: any) {
    payload.event.target.closest('th').classList.remove('is-selected');
    const droppedOnColumnIndex = payload.index;
    let newColumns: GridColumn[] = JSON.parse(JSON.stringify(this.columns));
    newColumns.splice(droppedOnColumnIndex, 0, newColumns.splice(this.draggingColumnIndex, 1)[0]);
    this.setColumns([]);
    setTimeout(() => {
      this.setColumns(newColumns);
    });
  }

  //#endregion Columns drag and drop
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-content {

  .table thead th,
  .table tbody td {

    &.cell-channel-metadata-type-string,
    &.cell-title {
      &>div {
        align-items: center;
        display: flex;

        .icon-w {
          display: flex;
          margin-inline-end: .5rem;
          max-width: 1rem;
        }
      }
    }

    &.cell-title {
      &>div {
        .text.active {
          color: $primary;
          cursor: pointer;
        }
      }
    }

    .campaign-status {
      align-items: center;
      display: flex;

      .campaign-name-status {
        display: flex;
        align-items: center;
        font-size: 14px;

        @media (max-width: 768px) {
          justify-content: flex-end;
        }

        .indicator {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: 1.5px solid #aaa;

          @media (max-width: 768px) {
            order: 2;
            margin: 0 5px 0 0;
          }

          &.indicator-1 {
            background-color: $success;
          }

          &.indicator-2 {
            border-color: $success;
          }

          &.indicator-3 {
            border-color: $danger;
          }

          &.indicator-4 {
            background: $lightd;
            border-color: $lightd;
          }

          &.indicator-5 {
            background: $danger;
            border-color: $danger;
          }
        }
      }

      .status-str-text {
        font-size: .9rem;
        margin-inline-start: .3rem;

        &.clickable {
          color: $danger;
          cursor: pointer;
        }
      }
    }
  }

  .actions-list {
    .action {
      &.action-alerts {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon-w {
          line-height: 0;
        }

        .tag.is-small {
          position: absolute;
          left: 50%;
          margin-left: 1px;
          top: -11px;
          height: 19px;
          line-height: 12px;
          border: 2px solid #fff;
          border-radius: 4px;
          padding: 0;
          width: auto;

          .tag-i {
            border: 1px solid $danger;
            background: #fff;
            color: $danger;
            border-radius: 2px;
            padding: 0 2px;
            font-weight: 600;
          }
        }
      }

      &.action-phone {
        .icon-mobile-yes {
          color: $success;
        }

        .icon-mobile-no {
          color: $danger;
        }
      }
    }

    .tooltip-phone {
      .tooltip-content {
        transform: translateX(-20%);

        &:before {
          left: 20%;
        }
      }
    }
  }
}

html[dir="ltr"] {
  .grid-content {
    .actions-list {
      .tooltip-phone {
        .tooltip-content {
          transform: translateX(-80%);

          &:before {
            left: 80%;
          }
        }
      }
    }
  }
}</style>
